import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Input, Card, Tag } from 'antd';
import { SearchOutlined, EyeOutlined, DollarOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import ClaimStatusTag from '../components/ClaimStatusTag';
import ClaimDetailDrawer from '../components/ClaimDetailDrawer';
import PaymentProofModal from '../components/PaymentProofModal';
import { getAllClaims, markPaid, getClaimDetail } from '../costsClaimAction';
import costsClaimApi from 'api/costsClaimApi';

export const ClaimPaymentList: React.FC = () => {
  const dispatch = useDispatch();
  const { allClaims, loading, currentClaim } = useSelector((state: any) => state.costsClaim || {});

  const [searchKw, setSearchKw] = useState('');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [targetClaim, setTargetClaim] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, [dispatch]);

  const loadData = (kw = searchKw) => {
    const params: any = { page: 1, perPage: 50, status: 'APPROVED_BY_FINANCE' };
    if (kw) params.keyword = kw;
    dispatch(getAllClaims(params) as any);
  };

  const handleOpenPaymentModal = (claim: any) => {
    setTargetClaim(claim);
    setPaymentModalVisible(true);
  };

  const handleConfirmPayment = async (paymentNote: string, file: any) => {
    if (!targetClaim) return;

    // 1. Mark paid
    await dispatch(markPaid({ id: targetClaim.id, body: { paymentNote } }) as any);

    // 2. Upload UNC if provided
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      await costsClaimApi.uploadPaymentProof(targetClaim.id, formData);
    }

    setPaymentModalVisible(false);
    setDrawerVisible(false);
    loadData();
  };

  const handleViewDetail = (record: any) => {
    dispatch(getClaimDetail(record.id) as any);
    setDrawerVisible(true);
  };

  const columns = [
    {
      title: 'Mã Claim',
      dataIndex: 'claimCode',
      key: 'claimCode',
      width: 150,
      render: (text: string, record: any) => (
        <Button
          type="link"
          onClick={() => handleViewDetail(record)}
          style={{ padding: 0, fontWeight: 600 }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Người hưởng thụ',
      dataIndex: 'beneficiaryName',
      key: 'beneficiaryName',
      render: (val: string, record: any) => val || record.creatorName || record.createdBy || '-',
    },
    {
      title: 'Ngân hàng',
      dataIndex: 'bank',
      key: 'bank',
      width: 120,
      render: (bank: any) => (bank ? `${bank.code}` : '-'),
    },
    {
      title: 'Số TK',
      dataIndex: 'targetAccountNumber',
      key: 'targetAccountNumber',
      width: 150,
      render: (val: string) => val || '-',
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      render: (val: number, record: any) => (
        <span style={{ fontWeight: 'bold', color: '#52c41a', fontSize: 14 }}>
          {val ? `${Number(val).toLocaleString('vi-VN')} ${record.currency || 'VND'}` : '0 VND'}
        </span>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 170,
      render: (st: string) => <ClaimStatusTag status={st} />,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 130,
      align: 'center' as const,
      render: (_: any, record: any) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined style={{ color: '#1890ff' }} />}
            onClick={() => handleViewDetail(record)}
            title="Xem chi tiết"
          />

          {record.status === 'APPROVED_BY_FINANCE' && (
            <Button
              type="primary"
              size="small"
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              icon={<DollarOutlined />}
              onClick={() => handleOpenPaymentModal(record)}
              title="Xác nhận Thanh toán"
            >
              Thanh toán
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card title="Danh sách Claim Chờ Thanh Toán (Kế toán / Finance)">
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="Tìm theo mã / tiêu đề..."
            prefix={<SearchOutlined />}
            allowClear
            value={searchKw}
            onChange={(e) => {
              const val = e.target.value;
              setSearchKw(val);
              if (!val) {
                loadData('');
              }
            }}
            onPressEnter={() => loadData(searchKw)}
            style={{ width: 250 }}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={() => loadData(searchKw)}>
            Tìm kiếm
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={allClaims}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <ClaimDetailDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        claim={currentClaim}
        canMarkPaid={true}
        onMarkPaid={(c) => handleOpenPaymentModal(c)}
      />

      <PaymentProofModal
        visible={paymentModalVisible}
        onCancel={() => setPaymentModalVisible(false)}
        onSubmit={handleConfirmPayment}
        loading={loading}
      />
    </div>
  );
};

export default ClaimPaymentList;
